/**
 * Dropdown — generic dropdown manager. Delegated click on document.body for
 * `[data-action=toggle-dropdown]` / `[data-action=toggle-desktop-dropdown]` /
 * `[data-action=toggle-mobile-dropdown]` (breakpoint-specific). Uses
 * Collapse.slideDown/Up for open/close transitions. Auto-closes
 * other open dropdowns unless they have `.do-not-close`. Skips dropdowns
 * inside `<product-component>` (those are handled by ProductElement).
 *
 * Loaded as ES module via `@theme/dropdown` importmap entry.
 * Exports both the class and a pre-built `allDropdowns` singleton.
 * Module is loaded globally from `layout/theme.liquid` so the singleton
 * registers its document-level listeners on every page.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/responsive
 *   - @theme/collapse
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { Collapse } from '@theme/collapse';

export class Dropdown {
  constructor() {
    this.domDelegate = new Delegate(document.body);
    this.breakpoint = responsive.getCurrentBreakpoint();
    this.allDropdowns = document.querySelectorAll('.Dropdown');
    this.pageOverlayElement = document.querySelector('.pageOverlay');
    this.lock = false;
    this.bindEvents();
  }

  bindEvents() {
    this.domDelegate.on(
      'click',
      '[data-action=toggle-dropdown]',
      this.toggleDropdown.bind(this)
    );

    this.domDelegate.on(
      'click',
      ".info__dropdown-button[data-action='toggle-dropdown']",
      this.toggleDropdown.bind(this)
    );

    let selector;
    switch (this.breakpoint) {
      case 'desktop':
        selector = '[data-action=toggle-desktop-dropdown]';
        break;
      case 'pocket':
        selector = '[data-action=toggle-mobile-dropdown]';
        break;
      default:
        selector = false;
    }

    if (selector) {
      this.domDelegate.on('click', selector, this.toggleDropdown.bind(this));
    }
  }

  updateAllDropdowns() {
    this.allDropdowns = document.querySelectorAll('.Dropdown');
  }

  openDropdown(elem, target) {
    if (this.lock) return;
    this.lock = true;

    const DropdownButton = elem.querySelector('.Dropdown--Button');
    const DropdownContent = elem.querySelector('.Dropdown--Content');

    const targetDropdown = target.closest('.Dropdown');

    this.allDropdowns.forEach((dropdown) => {
      if (
        dropdown !== targetDropdown &&
        !dropdown.contains(targetDropdown) &&
        dropdown.querySelector("[aria-expanded='true']") &&
        !dropdown.classList.contains('do-not-close')
      ) {
        this.closeDropdown(dropdown, true);
      }
    });

    const transitionEndHandler = (event) => {
      if (event.propertyName === 'height') {
        this.lock = false;
        DropdownContent.removeEventListener('transitionend', transitionEndHandler);
      }
    };

    DropdownContent.addEventListener('transitionend', transitionEndHandler);

    DropdownButton.setAttribute('aria-expanded', 'true');
    new Collapse(DropdownContent).slideDown();
  }

  closeAllDropdowns(namespace) {
    namespace.querySelectorAll('.Dropdown').forEach((dropdown) => {
      if (
        dropdown.querySelector("[aria-expanded='true']") &&
        !dropdown.classList.contains('do-not-close')
      ) {
        this.closeDropdown(dropdown);
      }
    });
  }

  closeDropdown(elem, ignore) {
    if (this.lock && !ignore) return;
    this.lock = true;

    const DropdownButton = elem.querySelector('.Dropdown--Button');
    const DropdownContent = elem.querySelector('.Dropdown--Content');

    const transitionEndHandler = (event) => {
      if (event.propertyName === 'height') {
        if (DropdownContent.getAttribute('aria-hidden') === 'false') {
          DropdownContent.setAttribute('aria-hidden', 'true');
        }
        this.lock = false;
        DropdownContent.removeEventListener('transitionend', transitionEndHandler);
      }
    };

    DropdownContent.addEventListener('transitionend', transitionEndHandler);

    new Collapse(DropdownContent).slideUp();
    DropdownButton.setAttribute('aria-expanded', false);
  }

  toggleDropdown(event, target) {
    event.preventDefault();

    if (target.closest('product-component')) return;

    const mainDropdown = target.closest('.Dropdown');
    // The toggle trigger may be the row wrapper (no aria-expanded) rather than
    // the button itself, so read state from the dropdown's own button.
    const stateButton = mainDropdown.querySelector('.Dropdown--Button') ?? target;
    const isOpen = stateButton.getAttribute('aria-expanded') === 'true';

    if (this.lock) return;

    if (isOpen) {
      this.closeDropdown(mainDropdown, target);
      if (mainDropdown.classList.contains('filter__dropdown')) {
        mainDropdown.dispatchEvent(new CustomEvent('close:filters', { bubbles: true }));
      }
    } else {
      this.openDropdown(mainDropdown, target);
      if (mainDropdown.classList.contains('filter__dropdown')) {
        mainDropdown.dispatchEvent(new CustomEvent('open:filters', { bubbles: true }));
      }
    }
  }
}

export const allDropdowns = new Dropdown();
window.allDropdowns = allDropdowns;
